import { useLocation, useParams } from "react-router";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { parseBrandSiteWorkbenchPath } from "@/lib/brand-site-routing";
import { findModule } from "@/lib/modules";

export type ShellMode = "enterprise" | "brand" | "product" | "account-admin" | "other";

export type ShellRouteInfo = {
  mode: ShellMode;
  accountId: string;
  brandId: string | null;
  appKey: string | null;
  /** 工具一级 nav（如 settings） */
  appSection: string | null;
  /** 设置大区等二级段（如 members） */
  appSubsection: string | null;
  /** AI 官网当前站（`sites/:siteId`）；旧路径为 null */
  brandSiteId: string | null;
  appName: string | null;
};

/** Pure helper for tests and non-hook callers. */
export function resolveShellRouteInfo(
  pathname: string,
  params: { accountId?: string; brandId?: string; appKey?: string },
  modules: Array<{ key: string; name: string }> = [],
): ShellRouteInfo {
  const accountId = params.accountId && params.accountId !== "new" ? params.accountId : "";

  const brandSite = parseBrandSiteWorkbenchPath(pathname);
  if (brandSite && brandSite.brandId !== "new") {
    const app = modules.find((module) => module.key === "brand_site");
    return {
      mode: "product",
      accountId,
      brandId: brandSite.brandId,
      appKey: "brand_site",
      appSection: brandSite.section,
      appSubsection: null,
      brandSiteId: brandSite.siteId,
      appName: app?.name ?? "brand_site",
    };
  }

  // 未进站的列表（含旧 /apps/brand_site/overview）留在品牌容器，不滑入产品面板
  const brandSiteList = pathname.match(
    /^\/accounts\/[^/]+\/brands\/([^/]+)\/(?:brand_site|apps\/brand_site(?:\/overview)?)\/?$/,
  );
  if (brandSiteList && brandSiteList[1] !== "new") {
    return {
      mode: "brand",
      accountId,
      brandId: brandSiteList[1],
      appKey: null,
      appSection: null,
      appSubsection: null,
      brandSiteId: null,
      appName: null,
    };
  }

  const productMatch = pathname.match(
    /^\/accounts\/[^/]+\/brands\/([^/]+)\/apps\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?/,
  );
  if (productMatch && productMatch[1] !== "new") {
    const appKey = productMatch[2];
    const app = modules.find((module) => module.key === appKey);
    return {
      mode: "product",
      accountId,
      brandId: productMatch[1],
      appKey,
      appSection: productMatch[3] || "overview",
      appSubsection: productMatch[4] || null,
      brandSiteId: null,
      appName: app?.name ?? appKey,
    };
  }

  // 单站素材库 — AI 官网产品面板（侧栏「站点素材库」）
  const brandSiteMaterialsMatch = pathname.match(
    /^\/accounts\/[^/]+\/brands\/([^/]+)\/sites\/([^/]+)\/materials(?:\/|$)/,
  );
  if (brandSiteMaterialsMatch && brandSiteMaterialsMatch[1] !== "new") {
    const app = modules.find((module) => module.key === "brand_site");
    return {
      mode: "product",
      accountId,
      brandId: brandSiteMaterialsMatch[1],
      appKey: "brand_site",
      appSection: "materials",
      appSubsection: null,
      brandSiteId: brandSiteMaterialsMatch[2],
      appName: app?.name ?? "brand_site",
    };
  }

  // Brand hub + brand content stack (materials / kb / inbox) — Option A + A1
  const brandMatch = pathname.match(/^\/accounts\/[^/]+\/brands\/([^/]+)(?:\/|$)/);
  if (brandMatch && brandMatch[1] !== "new" && brandMatch[1] !== "manage") {
    return {
      mode: "brand",
      accountId,
      brandId: brandMatch[1],
      appKey: null,
      appSection: null,
      appSubsection: null,
      brandSiteId: null,
      appName: null,
    };
  }

  if (/^\/accounts\/[^/]+\/?$/.test(pathname)) {
    return {
      mode: "enterprise",
      accountId,
      brandId: null,
      appKey: null,
      appSection: null,
      appSubsection: null,
      brandSiteId: null,
      appName: null,
    };
  }

  if (
    /\/(members|authorizations|received-authorizations|settings|subscriptions|wecom|channels|kb|materials|inbox|brands|audit-log)(\/|$)/.test(
      pathname,
    ) &&
    !/\/brands\/\d+/.test(pathname)
  ) {
    return {
      mode: "account-admin",
      accountId,
      brandId: null,
      appKey: null,
      appSection: null,
      appSubsection: null,
      brandSiteId: null,
      appName: null,
    };
  }

  return {
    mode: "other",
    accountId,
    brandId: null,
    appKey: null,
    appSection: null,
    appSubsection: null,
    brandSiteId: null,
    appName: null,
  };
}

export function useShellRouteInfo(): ShellRouteInfo {
  const { pathname } = useLocation();
  const params = useParams();
  const workspace = useOptionalAccountWorkspace();

  return resolveShellRouteInfo(
    pathname,
    {
      accountId: params.accountId,
      brandId: params.brandId,
      appKey: params.appKey,
    },
    workspace?.modules ?? [],
  );
}
